import { PERIODS } from "src/services/EventService";

const DRAFTKINGS_SC_ID = 1203;
const FANDUEL_SC_ID = 355;

type BookieMarket = Record<string, SportscubeBet[]>;

interface SportscubeBet {
  content: string;
  odd: number;
}

interface SportscubeOdds {
  bookieMarkets?: Record<string, BookieMarket>;
}

interface FallbackBook {
  name?: string;
  displayTitle?: string;
}

interface FallbackContext {
  lineType: string;
  favoriteLine?: string;
  book: FallbackBook;
  sportscubeOdds: unknown;
}

interface LineLike {
  [key: string]: number | undefined;
  awaySpread?: number;
  homeSpread?: number;
  awayMoney?: number;
  homeMoney?: number;
  drawMoney?: number;
  awayPrice?: number;
  homePrice?: number;
  total?: number;
  overPrice?: number;
  underPrice?: number;
}

interface ScoredSpreadPair {
  awayLine: number;
  awayOdd: number;
  homeLine: number;
  homeOdd: number;
  score: number;
}

interface ScoredTotalPair {
  total: number;
  overOdd: number;
  underOdd: number;
  score: number;
  delta: number;
}

const normalizeBookName = (value: string) => value.toLowerCase().replace(/[^a-z]/g, "");

const getSportscubeOddsObject = (sportscubeOdds: unknown): SportscubeOdds | null => {
  if (!sportscubeOdds) {
    return null;
  }

  if (typeof sportscubeOdds === "string") {
    try {
      return JSON.parse(sportscubeOdds);
    } catch (_err) {
      return null;
    }
  }

  if (typeof sportscubeOdds === "object") {
    return sportscubeOdds as SportscubeOdds;
  }

  return null;
};

const getPeriodFromLineType = (lineType: string) =>
  Object.values(PERIODS).find((period) => lineType.includes(period)) || "FG";

const getBetTypeFromLineType = (lineType: string, favoriteLine = "moneyline") => {
  if (lineType === "combined") {
    return favoriteLine;
  }

  const period = getPeriodFromLineType(lineType);
  return lineType.replace(period, "");
};

const getBookieId = (book: FallbackBook): number | null => {
  const normalized = normalizeBookName(`${book.name || ""}${book.displayTitle || ""}`);

  if (normalized.includes("draftkings")) {
    return DRAFTKINGS_SC_ID;
  }

  if (normalized.includes("fanduel") || normalized.includes("betmgm") || normalized === "mgm") {
    return FANDUEL_SC_ID;
  }

  return null;
};

const toAmericanOdds = (decimalOdd?: number): number | null => {
  if (!decimalOdd || decimalOdd <= 1) {
    return null;
  }

  if (decimalOdd >= 2) {
    return Math.round((decimalOdd - 1) * 100);
  }

  return Math.round(-100 / (decimalOdd - 1));
};

const parseNumericContent = (content?: string): number | null => {
  if (!content || content.includes("/")) {
    return null;
  }

  const parsed = parseFloat(content);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return parsed;
};

const getMoneylineFromMarket = (market?: SportscubeBet[]): LineLike | null => {
  if (!market?.length) {
    return null;
  }

  const findOdd = (label: string) => market.find((bet) => bet.content.toUpperCase() === label)?.odd;

  const away = toAmericanOdds(findOdd("WIN AWAY"));
  const home = toAmericanOdds(findOdd("WIN HOME"));
  const draw = toAmericanOdds(findOdd("DRAW"));

  if (away === null && home === null && draw === null) {
    return null;
  }

  const line: LineLike = {};
  if (away !== null) {
    line.awayMoney = away;
  }
  if (home !== null) {
    line.homeMoney = home;
  }
  if (draw !== null) {
    line.drawMoney = draw;
  }

  return line;
};

const getSpreadFromMarkets = (awayMarket?: SportscubeBet[], homeMarket?: SportscubeBet[]): LineLike | null => {
  if (!awayMarket?.length || !homeMarket?.length) {
    return null;
  }

  const awayCandidates = awayMarket
    .map((bet) => {
      const line = parseNumericContent(bet.content);
      return line === null ? null : { line, odd: bet.odd };
    })
    .filter((item): item is { line: number; odd: number } => Boolean(item));

  const homeCandidates = homeMarket
    .map((bet) => {
      const line = parseNumericContent(bet.content);
      return line === null ? null : { line, odd: bet.odd };
    })
    .filter((item): item is { line: number; odd: number } => Boolean(item));

  if (!awayCandidates.length || !homeCandidates.length) {
    return null;
  }

  const pairs: ScoredSpreadPair[] = [];

  awayCandidates.forEach((away) => {
    homeCandidates.forEach((home) => {
      if (Math.abs(away.line) !== Math.abs(home.line)) {
        return;
      }

      if (away.line * home.line > 0) {
        return;
      }

      pairs.push({
        awayLine: away.line,
        awayOdd: away.odd,
        homeLine: home.line,
        homeOdd: home.odd,
        score: Math.abs(away.odd - 2) + Math.abs(home.odd - 2),
      });
    });
  });

  if (!pairs.length) {
    return null;
  }

  pairs.sort((a, b) => {
    if (a.score !== b.score) {
      return a.score - b.score;
    }

    if (Math.abs(a.awayLine) !== Math.abs(b.awayLine)) {
      return Math.abs(a.awayLine) - Math.abs(b.awayLine);
    }

    return a.awayLine - b.awayLine;
  });

  const best = pairs[0];
  const awayPrice = toAmericanOdds(best.awayOdd);
  const homePrice = toAmericanOdds(best.homeOdd);

  if (awayPrice === null || homePrice === null) {
    return null;
  }

  return {
    awaySpread: best.awayLine,
    homeSpread: best.homeLine,
    awayPrice,
    homePrice,
  };
};

const getTotalFromMarkets = (overMarket?: SportscubeBet[], underMarket?: SportscubeBet[]): LineLike | null => {
  if (!overMarket?.length || !underMarket?.length) {
    return null;
  }

  const overMap = new Map<number, number>();
  overMarket.forEach((bet) => {
    const total = parseNumericContent(bet.content);
    if (total !== null) {
      overMap.set(total, bet.odd);
    }
  });

  const pairs: ScoredTotalPair[] = [];
  underMarket.forEach((bet) => {
    const total = parseNumericContent(bet.content);
    if (total === null) {
      return;
    }

    const overOdd = overMap.get(total);
    if (overOdd === undefined) {
      return;
    }

    pairs.push({
      total,
      overOdd,
      underOdd: bet.odd,
      score: Math.abs(overOdd - 2) + Math.abs(bet.odd - 2),
      delta: Math.abs(overOdd - bet.odd),
    });
  });

  if (!pairs.length) {
    return null;
  }

  pairs.sort((a, b) => {
    if (a.score !== b.score) {
      return a.score - b.score;
    }

    if (a.delta !== b.delta) {
      return a.delta - b.delta;
    }

    return a.total - b.total;
  });

  const best = pairs[0];
  const overPrice = toAmericanOdds(best.overOdd);
  const underPrice = toAmericanOdds(best.underOdd);

  if (overPrice === null || underPrice === null) {
    return null;
  }

  return {
    total: best.total,
    overPrice,
    underPrice,
  };
};

const getFallbackForBetType = (
  bookMarkets: BookieMarket | undefined,
  betType: string,
  period: string
): LineLike | null => {
  if (!bookMarkets) {
    return null;
  }

  if (betType === "moneyline") {
    const marketKey = period === "1H" ? "3W_HT" : "3W";
    return getMoneylineFromMarket(bookMarkets[marketKey]);
  }

  if (betType === "spread") {
    if (period !== "FG") {
      return null;
    }

    return getSpreadFromMarkets(bookMarkets.asian_handicap_away, bookMarkets.asian_handicap_home);
  }

  if (betType === "total") {
    if (period !== "FG") {
      return null;
    }

    return getTotalFromMarkets(bookMarkets.goals_over, bookMarkets.goals_under);
  }

  return null;
};

export const getSportscubeFallbackLine = ({
  lineType,
  favoriteLine,
  book,
  sportscubeOdds,
}: FallbackContext): LineLike | null => {
  const oddsObj = getSportscubeOddsObject(sportscubeOdds);
  const bookieId = getBookieId(book);

  if (!oddsObj?.bookieMarkets || bookieId === null) {
    return null;
  }

  const bookMarkets = oddsObj.bookieMarkets[String(bookieId)];
  if (!bookMarkets) {
    return null;
  }

  const period = getPeriodFromLineType(lineType);

  if (lineType === "combined") {
    return {
      ...getFallbackForBetType(bookMarkets, favoriteLine || "moneyline", period),
      ...getFallbackForBetType(bookMarkets, "total", period),
    };
  }

  const betType = getBetTypeFromLineType(lineType, favoriteLine);
  return getFallbackForBetType(bookMarkets, betType, period);
};

export const mergeLineWithFallback = (line: LineLike, fallbackLine: LineLike | null): LineLike => {
  if (!fallbackLine) {
    return line;
  }

  const merged: LineLike = { ...line };

  (Object.keys(fallbackLine) as (keyof LineLike)[]).forEach((key) => {
    if (merged[key] === undefined || merged[key] === null) {
      merged[key] = fallbackLine[key];
    }
  });

  return merged;
};
